import { useMemo } from 'react';
import {
  type DeviceInterfaceVlanItem,
  type DeviceVlanItem,
  useDeviceVlans,
} from '@/hooks/useDeviceVlans';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EmptyState } from '@/components/ui/empty-state';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import { classifyVlanMembership } from '@/lib/vlanMembership';
import { cn } from '@/lib/utils';

interface DeviceVlansTabProps {
  deviceId: string;
}

interface InterfaceGroup {
  interfaceId: string;
  interfaceName: string;
  interfaceType: string | null;
  adminStatus: string | null;
  operStatus: string | null;
  portMode: string | null;
  vlans: DeviceInterfaceVlanItem[];
}

// Collapse the flat (interface, vlan) rows from the API into one row
// per interface with a list of VLAN chips. The server already sorts
// by interface but Map's insertion order may shuffle when we add the
// second entry to an interface — final sort by name stabilises.
function groupByInterface(rows: DeviceInterfaceVlanItem[]): InterfaceGroup[] {
  const groups = new Map<string, InterfaceGroup>();
  for (const row of rows) {
    const existing = groups.get(row.interfaceId);
    if (existing) {
      existing.vlans.push(row);
    } else {
      groups.set(row.interfaceId, {
        interfaceId: row.interfaceId,
        interfaceName: row.interfaceName,
        interfaceType: row.interfaceType,
        adminStatus: row.adminStatus,
        operStatus: row.operStatus,
        // Every row for an interface carries the same interface-level mode, so the first
        // one is as good as any. This used to promote a port to 'trunk' if any of its
        // memberships said so, reconstructing client-side what the API could not tell it:
        // a port's mode follows from its whole membership set, and the old per-membership
        // value reported a trunk's native VLAN as 'access'. The server derives it now.
        portMode: row.interfacePortMode,
        vlans: [row],
      });
    }
  }
  return Array.from(groups.values()).sort((a, b) =>
    a.interfaceName.localeCompare(b.interfaceName),
  );
}

function VlanChip({ row }: { row: DeviceInterfaceVlanItem }) {
  // Read from the membership itself rather than from a port mode. Keying off the mode meant
  // a trunk's native VLAN never got its "N": that row's mode read 'access', so the branch
  // that draws the badge was never reached for the one VLAN it was written for.
  // Tagged VLAN → muted with "T"; native untagged VLAN → brand-soft with "N";
  // plain untagged VLAN (an access port) → brand-soft, no badge.
  const kind = classifyVlanMembership(row);
  const tagClass =
    kind === 'tagged' ? 'bg-muted text-muted-foreground' : 'bg-brand-soft text-brand';
  const badge = kind === 'native' ? 'N' : kind === 'tagged' ? 'T' : '';
  const label = row.vlanName
    ? `${String(row.vlanId)} (${row.vlanName})`
    : String(row.vlanId);
  return (
    <Badge className={cn('border-0 font-normal mr-1 mb-1', tagClass)} title={label}>
      {label}
      {badge ? <span className="ml-1 font-semibold">{badge}</span> : null}
    </Badge>
  );
}

const SUMMARY_COLUMNS: readonly ResponsiveTableColumn<DeviceVlanItem>[] = [
  {
    key: 'vlanId',
    label: 'VLAN',
    hideMobileLabel: true,
    render: (v) => <span className="font-mono font-medium">{v.vlanId}</span>,
  },
  {
    key: 'name',
    label: 'Name',
    render: (v) => v.vlanName ?? <span className="text-muted-foreground">—</span>,
  },
  {
    key: 'type',
    label: 'Type',
    render: (v) => v.vlanType ?? <span className="text-muted-foreground">—</span>,
    hideAt: 'mobile',
  },
  {
    key: 'status',
    label: 'Status',
    render: (v) => (
      <Badge
        className={cn(
          'border-0 font-normal',
          v.status === 'active'
            ? 'bg-sev-4/15 text-sev-4'
            : 'bg-muted text-muted-foreground',
        )}
      >
        {v.status}
      </Badge>
    ),
  },
  {
    key: 'access',
    label: 'Access',
    align: 'right',
    hideAt: 'mobile',
    render: (v) => <span className="font-mono">{v.accessPortCount}</span>,
  },
  {
    key: 'trunkNative',
    label: 'Trunk native',
    align: 'right',
    hideAt: 'mobile',
    render: (v) => <span className="font-mono">{v.trunkNativePortCount}</span>,
  },
  {
    key: 'trunkTagged',
    label: 'Trunk tagged',
    align: 'right',
    hideAt: 'mobile',
    render: (v) => <span className="font-mono">{v.trunkTaggedPortCount}</span>,
  },
  {
    key: 'total',
    label: 'Total ports',
    align: 'right',
    render: (v) => <span className="font-mono font-medium">{v.portCount}</span>,
  },
];

const MATRIX_COLUMNS: readonly ResponsiveTableColumn<InterfaceGroup>[] = [
  {
    key: 'interface',
    label: 'Interface',
    hideMobileLabel: true,
    render: (g) => <span className="font-mono font-medium">{g.interfaceName}</span>,
  },
  {
    key: 'admin',
    label: 'Admin',
    hideAt: 'mobile',
    render: (g) => g.adminStatus ?? <span className="text-muted-foreground">—</span>,
  },
  {
    key: 'oper',
    label: 'Oper',
    hideAt: 'mobile',
    render: (g) => g.operStatus ?? <span className="text-muted-foreground">—</span>,
  },
  {
    key: 'mode',
    label: 'Mode',
    render: (g) =>
      g.portMode ? (
        <Badge
          className={cn(
            'border-0 font-normal',
            g.portMode === 'trunk'
              ? 'bg-sev-3/20 text-foreground'
              : 'bg-brand-soft text-brand',
          )}
        >
          {g.portMode}
        </Badge>
      ) : (
        // Not yet determined — the device has not been polled by a gateway that derives it.
        // Deliberately not shown as 'access': that guess is exactly what used to be wrong.
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    key: 'vlans',
    label: 'VLANs',
    render: (g) => {
      const sorted = [...g.vlans].sort((a, b) => {
        if (a.isNative !== b.isNative) return a.isNative ? -1 : 1;
        return a.vlanId - b.vlanId;
      });
      return (
        <div className="flex flex-wrap">
          {sorted.map((row) => (
            <VlanChip key={`${row.interfaceId}-${String(row.vlanId)}`} row={row} />
          ))}
        </div>
      );
    },
  },
];

// Natural order so Gi1/0/2 sorts before Gi1/0/10.
function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

function PortGroup({ label, names }: { label: string; names: string[] }) {
  if (names.length === 0) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:gap-4">
      <span className="text-muted-foreground font-medium sm:w-36 sm:flex-shrink-0">
        {label} ({names.length})
      </span>
      <div className="flex flex-wrap gap-1 min-w-0">
        {names.map((n) => (
          <Badge key={n} className="border-0 font-normal font-mono bg-muted text-muted-foreground">
            {n}
          </Badge>
        ))}
      </div>
    </div>
  );
}

// Expanded-row detail for a VLAN: the ports carrying it, split by membership to
// mirror the access / trunk-native / trunk-tagged count columns on the row.
function VlanPortsDetail({
  vlan,
  interfaceVlans,
}: {
  vlan: DeviceVlanItem;
  interfaceVlans: DeviceInterfaceVlanItem[];
}) {
  const groups = useMemo(() => {
    const access: string[] = [];
    const native: string[] = [];
    const tagged: string[] = [];
    for (const r of interfaceVlans) {
      if (r.vlanId !== vlan.vlanId) continue;
      // Bucketed by what the membership is, not by the port's mode. Keying off the mode put
      // every trunk's native VLAN in the access bucket, because that row's mode read
      // 'access' — so a trunk port appeared, on the VLAN it carries untagged, to be an
      // access port.
      const kind = classifyVlanMembership(r);
      if (kind === 'tagged') tagged.push(r.interfaceName);
      else if (kind === 'native') native.push(r.interfaceName);
      else access.push(r.interfaceName);
    }
    access.sort(naturalCompare);
    native.sort(naturalCompare);
    tagged.sort(naturalCompare);
    return { access, native, tagged };
  }, [interfaceVlans, vlan.vlanId]);

  const total = groups.access.length + groups.native.length + groups.tagged.length;

  return (
    <div className="space-y-2 text-sm">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span>
          Type <span className="text-foreground">{vlan.vlanType ?? '—'}</span>
        </span>
        <span>
          Status <span className="text-foreground">{vlan.status}</span>
        </span>
      </div>
      {total === 0 ? (
        <p className="text-muted-foreground">No ports carry this VLAN.</p>
      ) : (
        <div className="space-y-1.5">
          <PortGroup label="Untagged / access" names={groups.access} />
          <PortGroup label="Trunk native" names={groups.native} />
          <PortGroup label="Trunk tagged" names={groups.tagged} />
        </div>
      )}
    </div>
  );
}

export default function DeviceVlansTab({ deviceId }: DeviceVlansTabProps) {
  const { data, isLoading, isError } = useDeviceVlans(deviceId);

  const groups = useMemo(
    () => (data ? groupByInterface(data.interfaceVlans) : []),
    [data],
  );

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (isError || !data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load VLAN data.</AlertDescription>
      </Alert>
    );
  }

  const noData = data.vlans.length === 0 && data.interfaceVlans.length === 0;

  if (noData) {
    return (
      <section className="bg-card border border-border rounded-lg p-5">
        <EmptyState
          variant="no-data"
          title="No VLAN data yet"
          description="If this is a managed switch, ensure SNMP is configured and Q-BRIDGE-MIB (.1.3.6.1.2.1.17) is reachable."
          size="inline"
        />
      </section>
    );
  }

  return (
    <div className="space-y-4 md:space-y-5">
      <section className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">VLANs on this device</h2>
          <span className="text-xs text-muted-foreground">
            {data.interfaceVlans.length > 0 ? (
              <span className="hidden sm:inline">Select a VLAN to see its ports · </span>
            ) : null}
            {String(data.vlans.length)} VLAN{data.vlans.length === 1 ? '' : 's'}
          </span>
        </div>
        {data.vlans.length === 0 ? (
          <p className="text-sm text-muted-foreground">No VLANs reported for this device.</p>
        ) : (
          <ResponsiveTable<DeviceVlanItem>
            columns={SUMMARY_COLUMNS}
            rows={data.vlans}
            rowKey={(r) => r.id}
            renderExpanded={(v) =>
              v.portCount > 0 ? (
                <VlanPortsDetail vlan={v} interfaceVlans={data.interfaceVlans} />
              ) : null
            }
          />
        )}
      </section>

      <section className="bg-card border border-border rounded-lg p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h2 className="text-sm font-semibold">Interface ↔ VLAN assignments</h2>
          <p className="text-xs text-muted-foreground text-right hidden sm:block">
            <Badge className="bg-brand-soft text-brand border-0 mr-1">VLAN</Badge>
            access / trunk-tagged ·{' '}
            <Badge className="bg-brand-soft text-brand border-0 mr-1">
              VLAN <span className="font-semibold ml-0.5">N</span>
            </Badge>
            trunk native
          </p>
        </div>
        {groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No interface-level VLAN assignments reported. The device may not have any ports
            yet, or VLAN data has not been collected.
          </p>
        ) : (
          <ResponsiveTable<InterfaceGroup>
            columns={MATRIX_COLUMNS}
            rows={groups}
            rowKey={(r) => r.interfaceId}
          />
        )}
      </section>
    </div>
  );
}
